import { WalletError } from '@cef-ai/wallet-api-client';

const FEATURE = 'publickey-credentials-get';

interface Policy {
  allowsFeature(feature: string, origin?: string): boolean;
}

interface PolicyHost {
  featurePolicy?: unknown;
  permissionsPolicy?: unknown;
}

/**
 * Chromium exposes the policy object under either spelling; other engines under
 * neither. Each candidate is tried in turn rather than
 * `featurePolicy ?? permissionsPolicy`, because `??` commits to `featurePolicy`
 * the moment it is merely non-nullish — so an engine part-way through the
 * rename that leaves the legacy name behind as a stub with no `allowsFeature`
 * would be reported as unable to answer on a browser that could in fact answer
 * under the other spelling.
 *
 * Deliberately duplicated from `policyOf` in `@cef-ai/wallet`'s
 * `ceremonyRefusal.ts` rather than shared: that module is the HOST SDK's, and
 * exporting a frame-side helper from a package published to host applications
 * would add public API that means nothing to a host. The two answer different
 * questions (see below); only this six-line accessor overlaps.
 */
function policyOf(host: unknown): Policy | null {
  const h = host as PolicyHost | null | undefined;
  for (const candidate of [h?.featurePolicy, h?.permissionsPolicy]) {
    const p = candidate as Partial<Policy> | undefined;
    if (typeof p?.allowsFeature === 'function') return p as Policy;
  }
  return null;
}

/**
 * May THIS document run `publickey-credentials-get`? `true`/`false` when the
 * browser can answer, `null` when it has no Permissions Policy API at all.
 *
 * **The one-argument form, asked by the framed document about itself.** Note
 * the symmetry with `iframeCeremonyAllowed` in `@cef-ai/wallet`: the same
 * one-argument `allowsFeature(FEATURE)` call is *wrong* on the host — measured
 * on real Chromium it answers `true` for a missing `allow` attribute, for an
 * `allow` naming the wrong origin, and under a `publickey-credentials-get=(self)`
 * header, because it answers about the HOST'S OWN origin — and *right* here,
 * because here the asking document IS the wallet frame. Chromium folds the
 * container policy (the `allow` attribute the host wrote) and the parent's own
 * policy into the framed document's effective policy, so this is the same
 * question `navigator.credentials.get()` is about to consult, asked of the same
 * document.
 *
 * That is why the classification lives on this side of the wire and not on the
 * host's: the host must reason about a frame it is not inside; the frame can
 * simply ask.
 *
 * `null` is deliberately distinct from `false`: a browser with no
 * `featurePolicy`/`permissionsPolicy` object (Safari, Firefox) has not denied
 * anything.
 */
export function ownDocumentCeremonyAllowed(doc: unknown): boolean | null {
  const policy = policyOf(doc);
  if (!policy) return null;
  try {
    return policy.allowsFeature(FEATURE) === true;
  } catch {
    // A call an engine does not implement the way we assumed. "Cannot answer",
    // never "denied".
    return null;
  }
}

/**
 * Turn the rejection of an in-surface WebAuthn ceremony into the error that
 * should cross the `wallet:error` wire, and return it for the caller to throw.
 *
 * A Permissions Policy denial and a reader dismissing the passkey sheet both
 * arrive as a `NotAllowedError`, and they need opposite responses from the SDK:
 * the first must be retried on another surface, the second must not. The
 * distinction has to travel as a `code`, because that is the only field of a
 * thrown error that survives the bridge — `PopupHostBridge` maps a thrown
 * `WalletError` to `wallet:error{code}` and everything else to
 * `wallet:error{code:'internal'}`, and `IframeTransport` rebuilds it as a
 * `WalletError` whose `name` is `'WalletError'`. Classifying host-side on
 * `err.name`, as the SDK first tried, therefore cannot work: by the time the
 * host sees it the name is gone.
 *
 * Three outcomes, and the third is the point:
 *
 *   - the frame may NOT use the feature → `ceremony-policy-denied`. The SDK
 *     falls back to a popup.
 *   - the frame MAY use the feature, so nothing was denied → `user-cancelled`.
 *     The SDK rethrows without re-prompting. **That code is "the reader did not
 *     complete the prompt", not strictly "the reader dismissed it":** WebAuthn
 *     reports a dismissal and a ceremony TIMEOUT as the same `NotAllowedError`
 *     with no field separating them, so this branch necessarily covers both.
 *     The conflation is the platform's; inventing a distinction it does not
 *     give us would be worse than naming the union honestly, and the remedy is
 *     the same either way — offer the action again.
 *   - the browser cannot answer (Safari, Firefox: no Permissions Policy API)
 *     → **the original error, untouched.** We know nothing, so we claim
 *     nothing: it crosses as `internal`, exactly as it did before S1, and the
 *     SDK rethrows. Guessing `user-cancelled` would put a claim about the
 *     reader's intent on the wire that no measurement supports; guessing
 *     `ceremony-policy-denied` would re-prompt every reader who simply
 *     declined, in a popup, on the two engines where most declines happen.
 *
 * Correct on the popup surface too, and for the same reason rather than by
 * exception: a top-level wallet document is `self` for its own feature, so
 * Chromium answers `true` there and a dismissed popup sheet classifies as
 * `user-cancelled`.
 */
export function classifyCeremonyRefusal(
  err: unknown,
  doc: unknown = (globalThis as { document?: unknown }).document,
): unknown {
  if ((err as { name?: string })?.name !== 'NotAllowedError') return err;
  const allowed = ownDocumentCeremonyAllowed(doc);
  if (allowed === null) return err;
  const detail = err instanceof Error ? err.message : String(err);
  return allowed
    ? // "not completed", not "dismissed": the browser gives one error for a
      // dismissal and for a timeout, so the detail must not claim the reader
      // chose anything. `detail` carries the browser's own message, which is
      // the only place the difference could ever show up.
      new WalletError('user-cancelled', `the passkey prompt was not completed: ${detail}`)
    : new WalletError(
        'ceremony-policy-denied',
        `Permissions Policy does not grant "${FEATURE}" to this wallet surface: ${detail}`,
      );
}
